import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/stores/auth';
import type { AdminAuditLogsResponse } from '@/lib/stores/admin/types';
import { AdminLayout } from '@/components/layouts/admin-layout';
import FilterBar from '@/components/common/filter-bar';
import { AUDIT_LOG_FILTERS } from '@/lib/shared-types/filters';
import {
  FileText,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';

// Audit log interface (matches component needs)
interface AuditLog {
  id: string;
  timestamp: string;
  userId?: number;
  userName?: string;
  eventType: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  action: string;
  resourceType?: string;
  resourceId?: string;
  details?: string;
  ipAddress?: string;
  success: boolean;
}

interface AuditLogsResponse {
  success: boolean;
  data?: {
    logs: AuditLog[];
    total: number;
    page: number;
    pageSize: number;
  };
  error?: string;
}

export default function AuditLogsPage() {
  const { isInitialized } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [logTab, setLogTab] = useState<'all' | 'success' | 'warnings' | 'failed'>('all');
  const pageSize = 50;

  // Filter state managed by FilterBar - initialize from URL
  const [filters, setFilters] = useState<Record<string, string | string[]>>(() => {
    const initialFilters: Record<string, string | string[]> = {};
    
    // Read filters from URL
    const search = searchParams.get('search');
    const eventType = searchParams.get('eventType');
    const resourceType = searchParams.get('resourceType');
    const severity = searchParams.get('severity');
    const dateRange = searchParams.get('dateRange'); // Format: "start,end" or just "start"
    
    if (search) initialFilters.search = search;
    if (eventType && eventType !== 'all') initialFilters.eventType = eventType;
    if (resourceType && resourceType !== 'all') initialFilters.resourceType = resourceType;
    if (severity && severity !== 'all') initialFilters.severity = severity;
    if (dateRange) {
      const dates = dateRange.split(',');
      if (dates.length >= 1) {
        initialFilters.dateRange = dates.length === 2 ? dates : [dates[0], ''];
      }
    }
    
    return initialFilters;
  });

  useEffect(() => {
    setPage(1);
  }, [logTab, filters]);

  useEffect(() => {
    if (isInitialized) {
      fetchAuditLogs();
    }
  }, [page, isInitialized, filters, logTab]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build API parameters from filters
      const apiParams: Record<string, string> = {
        page: page.toString(),
        limit: pageSize.toString()
      };

      // Handle search
      if (filters.search && typeof filters.search === 'string') {
        apiParams.search = filters.search;
      }

      // Handle eventType (exclude 'all')
      if (filters.eventType && typeof filters.eventType === 'string' && filters.eventType !== 'all') {
        apiParams.eventType = filters.eventType;
      }

      // Handle resourceType (exclude 'all')
      if (filters.resourceType && typeof filters.resourceType === 'string' && filters.resourceType !== 'all') {
        apiParams.resourceType = filters.resourceType;
      }

      // Handle severity (exclude 'all') — tab overrides manual severity filter
      if (logTab === 'warnings') {
        apiParams.severity = 'high';
      } else if (filters.severity && typeof filters.severity === 'string' && filters.severity !== 'all') {
        apiParams.severity = filters.severity;
      }

      // Handle success/failed tab
      if (logTab === 'success') {
        apiParams.success = 'true';
      } else if (logTab === 'failed') {
        apiParams.success = 'false';
      }

      // Handle dateRange - convert to startDate and endDate
      if (filters.dateRange && Array.isArray(filters.dateRange)) {
        const [startDate, endDate] = filters.dateRange;
        if (startDate) {
          apiParams.startDate = startDate;
        }
        if (endDate) {
          apiParams.endDate = endDate;
        }
      }

      const params = new URLSearchParams(apiParams);
      const response = await api.get<AdminAuditLogsResponse>(`/api/admin/audit-logs?${params}`);

      if (response.success && response.data) {
        // Transform API response to match component interface
        const transformedLogs: AuditLog[] = (response.data.logs ?? []).map(log => ({
          id: log.id.toString(), // Convert number to string
          timestamp: log.timestamp,
          userId: log.userId || undefined,
          userName: log.userName,
          eventType: log.eventType,
          severity: log.severity,
          action: log.action,
          resourceType: log.resourceType || undefined,
          resourceId: log.resourceId || undefined,
          details: log.details || undefined,
          ipAddress: log.ipAddress || undefined,
          success: Boolean(log.success), // Convert number to boolean
        }));

        setLogs(transformedLogs);
        setTotal(response.data.pagination?.total || 0);
      } else {
        setError(response.error?.message || 'Failed to load audit logs');
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityBadge = (severity: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      'low': 'outline',
      'medium': 'secondary',
      'high': 'default',
      'critical': 'destructive'
    };

    const icons = {
      'low': <CheckCircle className="h-3 w-3" />,
      'medium': <AlertTriangle className="h-3 w-3" />,
      'high': <AlertTriangle className="h-3 w-3" />,
      'critical': <XCircle className="h-3 w-3" />
    };

    return (
      <Badge variant={variants[severity] || 'outline'} className="flex items-center gap-1">
        {icons[severity as keyof typeof icons]}
        {severity}
      </Badge>
    );
  };

  const getEventTypeBadge = (eventType: string) => {
    return <Badge variant="outline">{eventType.replace('_', ' ')}</Badge>;
  };

  const getSuccessIcon = (success: boolean) => {
    return success ? (
      <CheckCircle className="h-4 w-4 text-green-600" />
    ) : (
      <XCircle className="h-4 w-4 text-red-600" />
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <AdminLayout>
      {/* Status Tabs */}
      <Tabs value={logTab} onValueChange={(v) => setLogTab(v as typeof logTab)}>
        <TabsList>
          <TabsTrigger value="all">All ({total})</TabsTrigger>
          <TabsTrigger value="success">Successful</TabsTrigger>
          <TabsTrigger value="warnings">Warnings</TabsTrigger>
          <TabsTrigger value="failed">Failed</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="p-2 sm:p-3 @[740px]/admin-layout:p-4">
        <FilterBar
          filters={AUDIT_LOG_FILTERS}
          activeFilters={filters}
          onFilterChange={setFilters}
        />
      </div>

      {/* Audit Logs Table */}
      <section className="space-y-3">
        <div className="px-4 text-sm text-muted-foreground sm:px-6">
          {logs.length} events
        </div>
        <div className="px-4 sm:px-6">
          {logs.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No audit logs found</h3>
              <p className="text-muted-foreground">
                {Object.keys(filters).length > 0
                  ? 'Try adjusting your search or filters'
                  : 'Audit logs will appear here as system events occur'
                }
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{formatDate(log.timestamp)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {log.userName && log.userId ? (
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src="" />
                            <AvatarFallback className="text-xs">
                              {log.userName.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <button
                            type="button"
                            className="text-sm hover:text-primary hover:underline focus:outline-none focus:underline cursor-pointer text-left"
                            onClick={() => navigate(`/admin/users/${log.userId}`)}
                            title={`View ${log.userName}'s profile`}
                          >
                            {log.userName}
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">System</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium">{log.action}</p>
                        <p className="text-xs text-muted-foreground">{log.resourceType}</p>
                      </div>
                    </TableCell>
                    <TableCell>{getSeverityBadge(log.severity)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getSuccessIcon(log.success)}
                        <span className="text-sm">{log.success ? 'Success' : 'Failed'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm max-w-xs truncate">
                        {log.details || log.ipAddress || 'No details'}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </section>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground px-4">
            Page {page} of {Math.ceil(total / pageSize)}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.min(Math.ceil(total / pageSize), p + 1))}
            disabled={page === Math.ceil(total / pageSize) || loading}
          >
            Next
          </Button>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="p-4">
            <p className="text-destructive text-center">{error}</p>
            <Button
              variant="outline"
              onClick={fetchAuditLogs}
              className="mt-2 w-full"
              disabled={loading}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}
